import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useOutletContext } from 'react-router'

import { PageLevelTableWrapper, TOAST_TYPE } from '@base'
import { Table as TableV2, useTableReducer } from '@baseV2'
import { FulfillmentPlanFilters, strings } from '@constants'
import { getAllFulfillmentPlansApi } from '@data'
import { showToast } from '@data/state/action'
import { paths } from '@routes'
import { displayName, formatPeriod } from '@utils'
import StatusChip from '@pages/Admin/Orders/ManageOrder/Shipment/StatusChip'
import { displayItemWeight } from '../AddDispatchPlanV2'
import { CopySpan } from '../style'

const twoLine = (primary, secondary) => (
	<div>
		<div>{primary ?? '-'}</div>
		<small>
			<b>{secondary ?? '-'}</b>
		</small>
	</div>
)

const cityState = (location) => [location?.city, location?.state].filter(Boolean).join(', ') || '-'

const FulfillmentList = () => {
	const navigate = useNavigate()
	const dispatch = useDispatch()
	const [state, tableDispatch, actions] = useTableReducer(FulfillmentPlanFilters)
	const { forceUpdate } = useOutletContext()
	const userId = useSelector((_state) => _state.getIn(['user', 'user', 'user_id']))

	useEffect(() => {
		if (state.loading) {
			return
		}
		tableDispatch(actions.fetchTableDataProgress())
		getAllFulfillmentPlansApi(userId, state.query.page, 10, state.query.filters)
			.then((response) => {
				tableDispatch(actions.fetchTableDataSuccess(response.data, response.count))
			})
			.catch((error) => tableDispatch(actions.fetchTableDataFailure(error)))
	}, [state.query, forceUpdate])

	const openFulfillment = (row) =>
		navigate(paths.admin_manage_enquiry_order_edit_fulfillment_plan_edit(row?.order_id, row?.shipment_request_id, row?.fulfillment_plan_id))

	const copyOrderId = (e, id) => {
		if (!id) {
			return
		}
		// the row itself navigates, so keep the click here
		e.stopPropagation()
		navigator.clipboard.writeText(id)
		dispatch(showToast(true, strings(['order', 'id', 'copied']), { type: TOAST_TYPE.INFO }))
	}

	const columns = useMemo(
		() =>
			[
				{
					label: `${strings(['order', 'id'])} / ${strings(['fulfillment', 'id'])}`,
					key: (data) => data,
					renderer: (data) =>
						twoLine(
							<CopySpan onClick={(e) => copyOrderId(e, data?.order_id)}>{data?.order_id ?? '-'}</CopySpan>,
							data?.fulfillment_plan_id
						),
					minWidth: 130,
					mobile: true,
				},
				{
					label: `${strings('buyer')} / ${strings(['shipment', 'location'])}`,
					key: (data) => data,
					renderer: (data) => twoLine(data?.buyer_name, cityState(data?.buyer_shipping_location)),
					minWidth: 140,
					mobile: true,
				},
				{
					label: `${strings('supplier')} / ${strings(['dispatch', 'location'])}`,
					key: (data) => data,
					renderer: (data) => twoLine(data?.supplier_name, cityState(data?.supplier_dispatch_location)),
					minWidth: 140,
					mobile: true,
				},
				{
					label: `${strings('invoiced')} / ${strings('ordered')}`,
					key: (data) => data,
					renderer: (data) =>
						`${displayItemWeight(data?.serviced_weight, data?.input_type)} / ${displayItemWeight(
							data?.ordered_weight,
							data?.input_type
						)}`,
					minWidth: 120,
					mobile: true,
				},
				{
					label: `${strings(['sales', 'person'])} / ${strings(['supply', 'person'])}`,
					key: (data) => data,
					renderer: (data) => twoLine(displayName(data?.sales_person), displayName(data?.supply_person)),
					minWidth: 120,
					mobile: true,
				},
				{ label: strings('logistics_handler'), key: 'logistics_handler', default: '-', minWidth: 100, mobile: true },
				{
					label: strings(['fulfillment', 'date']),
					key: (data) => data,
					renderer: (data) => formatPeriod(data?.start_date, data?.end_date),
					minWidth: 160,
					mobile: true,
				},
				{
					label: strings('status'),
					key: 'status',
					renderer: (data) => <StatusChip status={data} />,
					fitContent: true,
					mobile: true,
				},
				// Paginated table: client-side sort would only reorder the current page, so keep all columns non-sortable.
			].map((col) => ({ ...col, sortable: false })),
		[]
	)

	const onPageChangeHandler = (next = true) => {
		tableDispatch(actions.changePage(next))
	}

	return (
		<PageLevelTableWrapper>
			<TableV2
				data={state.data}
				columns={columns}
				loading={state.loading}
				rowsToDisplay={10}
				page={state.query.page}
				totalItemCount={state.total_count}
				onPageChange={onPageChangeHandler}
				onRowClick={openFulfillment}
				fullHeight
			/>
		</PageLevelTableWrapper>
	)
}

export default FulfillmentList
